// React
import { useEffect, useMemo, useRef, useState } from "react";

// Redux
import { useSelector } from "react-redux";

// Hooks
import useCacheCleaner from "@Common/hooks/CacheCleaner";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Components
import BodyLayer from "@Layouts/components/Layers/BodyLayer";
import CollapsibleLeftPanel from "@Layouts/components/CollapsibleLeftPanel";
import InventoryBody from "@DataPages/components/Inventory/InventoryBody";
import PayPeriodSideBar from "@Layouts/components/SideBars/PayPeriodSideBar";

// Types
import { RootState } from "@/redux/store";
import { IPayPeriod } from "@/Common/api/types/CommissionCycle";
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";
import { InventoryFilterItem, useInventoryContext } from "./contexts/InventoryFilterContext";
import { ISideTreeBadge } from "@/Layouts/components/SideBars/SideTreeView/interfaces";

const buildTreeId = ({ commissionCycleId, payPeriodId }: InventoryFilterItem) => `${commissionCycleId}_${payPeriodId}`;

const QUERY_IDENTIFIERS = ["inventory"];
export default function InventoryLayer() {
	const { addSubscription } = useSubscriptions();
	const { clearCache } = useCacheCleaner();

	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId;

	const lastSelected = useRef<IPayPeriod | null>({ _id: "" } as IPayPeriod);

	const [selection, setSelection] = useState<IPayPeriod | null>(null);

	const { filterItems } = useInventoryContext();

	const customBadges = useMemo(() => {
		return filterItems.reduce((acc, item) => {
			const id = buildTreeId(item);
			const currentBadge: ISideTreeBadge = acc[id] ?? { title: 0, active: true };
			return {
				...acc,
				[id]: { ...currentBadge, title: Number(currentBadge.title) + 1 },
			};
		}, {} as Record<string, ISideTreeBadge>);
	}, [filterItems]);

	const sideBar = useMemo(() => {
		const sideSelection = function (value: IPeriodTreeSelection, isActualCycle = false) {
			const selected = value as IPayPeriod;
			if (selected?._id !== lastSelected.current?._id) {
				lastSelected.current = selected;
				setSelection(selected);
				if (isActualCycle) {
					addSubscription(
						["inventory", selected._id],
						[
							{
								context: "onUpdateInventory",
								// query: onUpdateInventory,
								variables: {
									payPeriodId: selected.payPeriodId,
								},
							},
						]
					);
				}
			}
		};

		return (
			<PayPeriodSideBar callbackSelected={sideSelection} selectDefaultLastCommissionCycle customBadges={customBadges} defaultByDate />
		);
	}, [addSubscription, customBadges]);

	useEffect(() => {
		return () => {
			lastSelected.current = null;
			setSelection(null);
			clearCache(QUERY_IDENTIFIERS);
		};
	}, [locationId]);

	return (
		<BodyLayer floatingContent={<CollapsibleLeftPanel control={sideBar} />}>
			<InventoryBody selected={selection} />
		</BodyLayer>
	);
}
